import { Query } from 'react-apollo';
import React, { useState } from 'react';
import TimezoneHelper from 'lib/timezones';
import { getWeeklyTimeframeDateOnlyStrings } from 'coaching/lib/timeIntervals';

import CoachAvailabilityView from 'coaching/components/CoachAvailabilityView/CoachAvailabilityView';
import CenteredLoadingIndicator from 'components/CenteredLoadingIndicator';
import CoachingErrorReporter from 'coaching/CoachingErrorReporter';
import GET_COACH_AVAILABILITY_SCHEDULES_AND_AVAILABILITIES_BY_DAY_QUERY from 'coaching/queries/GetCoachAvailabilitySchedulesAndAvailabilitiesByDay.gql';
import {
  GetCoachAvailabilitySchedulesAndAvailabilitiesByDay,
  GetCoachAvailabilitySchedulesAndAvailabilitiesByDay_coaching_getCoachAvailabilitySchedules_schedules,
  GetCoachAvailabilitySchedulesAndAvailabilitiesByDay_coaching_getCoachAvailabilitySchedules_schedules_availabilityRules,
  GetCoachAvailabilitySchedulesAndAvailabilitiesByDay_coaching_getCoachAvailabilitySchedules_schedules_maxDailySessions,
  GetCoachAvailabilitySchedulesAndAvailabilitiesByDay_coaching_getCoachAvailabilitiesByDay_availabilities,
} from 'coaching/queries/__generated__/GetCoachAvailabilitySchedulesAndAvailabilitiesByDay';

export type CoachingAvailabilitySchedule = GetCoachAvailabilitySchedulesAndAvailabilitiesByDay_coaching_getCoachAvailabilitySchedules_schedules;
export type CoachingAvailabilityRule = GetCoachAvailabilitySchedulesAndAvailabilitiesByDay_coaching_getCoachAvailabilitySchedules_schedules_availabilityRules;
export type CoachingAvailabilityMaxDailySessions = GetCoachAvailabilitySchedulesAndAvailabilitiesByDay_coaching_getCoachAvailabilitySchedules_schedules_maxDailySessions;

export type SingleDayCoachAvailability = GetCoachAvailabilitySchedulesAndAvailabilitiesByDay_coaching_getCoachAvailabilitiesByDay_availabilities;

type State = {
  timezone: string;
  weekOffset: number;
};

export default function CoachAvailabilityContainer() {
  const [state, setState] = useState<State>({
    weekOffset: 0,
    // try and ascertain the user's timezone, and default to the first value
    // in the select options if we cannot
    timezone:
      TimezoneHelper.userTimezoneDefaultValue ?? TimezoneHelper.USTimezoneSelectOptions[0].value,
  });
  const advanceWeekOffset = () => {
    setState({ ...state, weekOffset: state.weekOffset + 1 });
  };

  const regressWeekOffset = () => {
    setState({ ...state, weekOffset: state.weekOffset - 1 });
  };

  // just hardcording these for now, will ultimately be computed from the week offset
  const { startDate, endDate } = getWeeklyTimeframeDateOnlyStrings(
    state.weekOffset,
    state.timezone
  );

  return (
    <Query<GetCoachAvailabilitySchedulesAndAvailabilitiesByDay>
      query={GET_COACH_AVAILABILITY_SCHEDULES_AND_AVAILABILITIES_BY_DAY_QUERY}
      variables={{ startDate, endDate }}
      fetchPolicy="network-only">
      {({ loading, error, data }) => {
        if (loading) {
          return <CenteredLoadingIndicator />;
        } else if (!data?.coaching_getCoachAvailabilitySchedules || error) {
          return (
            <CoachingErrorReporter
              error={error ?? 'Unable to load and render coach availabilities'}
            />
          );
        }

        const { schedules, coachTimezone } = data.coaching_getCoachAvailabilitySchedules;
        const availabilities = data.coaching_getCoachAvailabilitiesByDay?.availabilities ?? [];

        return (
          <CoachAvailabilityView
            schedules={schedules}
            coachTimezone={coachTimezone}
            availabilities={availabilities}
            weekOffset={state.weekOffset}
            onWeekForward={advanceWeekOffset}
            onWeekBack={regressWeekOffset}
            weekStartDate={startDate}
            weekEndDate={endDate}
            availabilityQuery={{
              query: GET_COACH_AVAILABILITY_SCHEDULES_AND_AVAILABILITIES_BY_DAY_QUERY,
              variables: {
                startDate,
                endDate,
              },
            }}
          />
        );
      }}
    </Query>
  );
}
